import { apiFetch } from "./client";
import {
  canonicalStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "./stocky_public_ids";

/** キット/コンポーネント選択用の variant 一覧行。 */
export interface BomVariantOption {
  variant_id: StockyPublicBigIntId;
  shopify_variant_id: StockyPublicBigIntId;
  sku: string | null;
  title: string | null;
  available: number;
}

/** キットを構成する原材料 variant 1件。 */
export interface BomComponentRow {
  component_variant_id: StockyPublicBigIntId;
  shopify_variant_id: StockyPublicBigIntId;
  sku: string | null;
  title: string | null;
  quantity_per: number;
  available: number;
  /** この原材料の在庫だけで作れるキット数（available // quantity_per）。 */
  buildable_from_this: number;
}

/** 1キットの構成（BOM）。 */
export interface KitBom {
  parent_variant_id: StockyPublicBigIntId;
  shopify_variant_id: StockyPublicBigIntId;
  sku: string | null;
  title: string | null;
  components: BomComponentRow[];
  /** 全コンポーネントのボトルネック＝実際に組み立てられるキット数。コンポーネント未設定なら null。 */
  buildable_quantity: number | null;
  limiting_component_variant_id: StockyPublicBigIntId | null;
}

export interface BomComponentInput {
  component_shopify_variant_id: StockyPublicBigIntId;
  quantity_per: number;
}

export type BomReplacementDraftProtocol =
  | "server_product_set_v1"
  | "legacy_native_create";

export interface BomStaleReplacementClaim {
  claim_token: string;
  should_create_product: boolean;
  source_shopify_variant_id: StockyPublicBigIntId;
  target_shopify_variant_id: StockyPublicBigIntId | null;
  draft_state: "not_started" | "creating" | "ready";
  draft_protocol: BomReplacementDraftProtocol;
  shopify_product_gid: string | null;
  shopify_product_id: StockyPublicBigIntId | null;
  shopify_variant_gid: string | null;
  edit_required: boolean;
}

export interface BomStaleReplacementDraft extends BomStaleReplacementClaim {
  all_components_current: boolean;
  components: BomComponentInput[];
}

/** Preserve the backend UUID byte-for-byte; never synthesize or normalize it. */
export function canonicalBomReplacementClaimToken(value: unknown): string {
  if (
    typeof value !== "string" ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(
      value,
    )
  ) {
    throw new TypeError("BOM replacement claim token is invalid");
  }
  return value;
}

function normalizeVariantOption(value: unknown): BomVariantOption {
  if (value === null || typeof value !== "object") {
    throw new TypeError("BOM variant response is invalid");
  }
  const option = value as Omit<
    BomVariantOption,
    "variant_id" | "shopify_variant_id"
  > & {
    variant_id: unknown;
    shopify_variant_id: unknown;
  };
  return {
    ...option,
    variant_id: canonicalStockyPublicBigIntId(option.variant_id),
    shopify_variant_id: canonicalStockyPublicBigIntId(
      option.shopify_variant_id,
    ),
  };
}

function normalizeComponentRow(value: unknown): BomComponentRow {
  if (value === null || typeof value !== "object") {
    throw new TypeError("BOM component response is invalid");
  }
  const component = value as Omit<
    BomComponentRow,
    "component_variant_id" | "shopify_variant_id"
  > & {
    component_variant_id: unknown;
    shopify_variant_id: unknown;
  };
  return {
    ...component,
    component_variant_id: canonicalStockyPublicBigIntId(
      component.component_variant_id,
    ),
    shopify_variant_id: canonicalStockyPublicBigIntId(
      component.shopify_variant_id,
    ),
  };
}

function normalizeKit(value: unknown): KitBom {
  if (value === null || typeof value !== "object") {
    throw new TypeError("BOM kit response is invalid");
  }
  const kit = value as Omit<
    KitBom,
    | "parent_variant_id"
    | "shopify_variant_id"
    | "components"
    | "limiting_component_variant_id"
  > & {
    parent_variant_id: unknown;
    shopify_variant_id: unknown;
    components: unknown;
    limiting_component_variant_id: unknown;
  };
  if (!Array.isArray(kit.components)) {
    throw new TypeError("BOM components response is invalid");
  }
  return {
    ...kit,
    parent_variant_id: canonicalStockyPublicBigIntId(kit.parent_variant_id),
    shopify_variant_id: canonicalStockyPublicBigIntId(
      kit.shopify_variant_id,
    ),
    components: kit.components.map(normalizeComponentRow),
    limiting_component_variant_id:
      kit.limiting_component_variant_id === null
        ? null
        : canonicalStockyPublicBigIntId(
            kit.limiting_component_variant_id,
          ),
  };
}

function normalizeShopifyGid(
  value: unknown,
  resource: "Product" | "ProductVariant",
): { gid: string; id: StockyPublicBigIntId } | null {
  if (value === null) return null;
  if (typeof value !== "string") {
    throw new TypeError(`Shopify ${resource} GID is invalid`);
  }
  const prefix = `gid://shopify/${resource}/`;
  if (!value.startsWith(prefix)) {
    throw new TypeError(`Shopify ${resource} GID is invalid`);
  }
  const id = canonicalStockyPublicBigIntId(value.slice(prefix.length));
  if (value !== `${prefix}${id}`) {
    throw new TypeError(`Shopify ${resource} GID is invalid`);
  }
  return { gid: value, id };
}

function normalizeReplacementClaim(
  value: unknown,
  sourceId: StockyPublicBigIntId,
): BomStaleReplacementClaim {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError("BOM replacement claim response is invalid");
  }
  const claim = value as Record<string, unknown>;
  if (typeof claim.should_create_product !== "boolean") {
    throw new TypeError("BOM replacement claim response is invalid");
  }
  // A pre-protocol backend omits every draft field. Treat that shape only as
  // legacy-native-create evidence; never infer permission to call productSet.
  const draftProtocol: BomReplacementDraftProtocol =
    claim.draft_protocol === undefined
      ? "legacy_native_create"
      : claim.draft_protocol === "server_product_set_v1" ||
          claim.draft_protocol === "legacy_native_create"
        ? claim.draft_protocol
        : (() => {
            throw new TypeError("BOM replacement claim response is invalid");
          })();
  const responseSourceId = canonicalStockyPublicBigIntId(
    claim.source_shopify_variant_id,
  );
  if (responseSourceId !== sourceId) {
    throw new TypeError("BOM replacement claim source is invalid");
  }
  const targetShopifyVariantId =
    claim.target_shopify_variant_id === null
      ? null
      : canonicalStockyPublicBigIntId(claim.target_shopify_variant_id);
  const draftState =
    claim.draft_state === undefined && draftProtocol === "legacy_native_create"
      ? targetShopifyVariantId === null
        ? "not_started"
        : "ready"
      : claim.draft_state;
  if (
    draftState !== "not_started" &&
    draftState !== "creating" &&
    draftState !== "ready"
  ) {
    throw new TypeError("BOM replacement claim response is invalid");
  }
  const editRequired =
    claim.edit_required === undefined && draftProtocol === "legacy_native_create"
      ? false
      : claim.edit_required;
  if (typeof editRequired !== "boolean") {
    throw new TypeError("BOM replacement claim response is invalid");
  }
  const product = normalizeShopifyGid(
    claim.shopify_product_gid === undefined &&
      draftProtocol === "legacy_native_create"
      ? null
      : claim.shopify_product_gid,
    "Product",
  );
  const variantValue =
    claim.shopify_variant_gid === undefined &&
    draftProtocol === "legacy_native_create"
      ? targetShopifyVariantId === null
        ? null
        : `gid://shopify/ProductVariant/${targetShopifyVariantId}`
      : claim.shopify_variant_gid;
  const variant = normalizeShopifyGid(
    variantValue,
    "ProductVariant",
  );
  const ready = draftState === "ready";
  const commonReadyStateIsValid =
    targetShopifyVariantId !== null &&
    variant !== null &&
    variant.id === targetShopifyVariantId;
  const serverStateIsValid = ready
    ? commonReadyStateIsValid && product !== null && editRequired
    : targetShopifyVariantId === null &&
      product === null &&
      variant === null &&
      !editRequired;
  const legacyStateIsValid = ready
    ? commonReadyStateIsValid && product === null && !editRequired
    : targetShopifyVariantId === null &&
      product === null &&
      variant === null &&
      !editRequired;
  if (
    (draftProtocol === "server_product_set_v1" &&
      !serverStateIsValid) ||
    (draftProtocol === "legacy_native_create" &&
      !legacyStateIsValid)
  ) {
    throw new TypeError("BOM replacement claim state is invalid");
  }
  return {
    claim_token: canonicalBomReplacementClaimToken(claim.claim_token),
    should_create_product: claim.should_create_product,
    source_shopify_variant_id: responseSourceId,
    target_shopify_variant_id: targetShopifyVariantId,
    draft_state: draftState,
    draft_protocol: draftProtocol,
    shopify_product_gid: product?.gid ?? null,
    shopify_product_id: product?.id ?? null,
    shopify_variant_gid: variant?.gid ?? null,
    edit_required: editRequired,
  };
}

function normalizeDraftComponents(value: unknown): BomComponentInput[] {
  if (!Array.isArray(value)) {
    throw new TypeError("BOM replacement draft components are invalid");
  }
  const components = value.map((raw) => {
    if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
      throw new TypeError("BOM replacement draft component is invalid");
    }
    const component = raw as Record<string, unknown>;
    if (
      typeof component.quantity_per !== "number" ||
      !Number.isSafeInteger(component.quantity_per) ||
      component.quantity_per < 1
    ) {
      throw new TypeError("BOM replacement draft component is invalid");
    }
    return {
      component_shopify_variant_id: canonicalStockyPublicBigIntId(
        component.component_shopify_variant_id,
      ),
      quantity_per: component.quantity_per,
    };
  });
  const ids = components.map((component) =>
    canonicalStockyPublicBigIntId(component.component_shopify_variant_id),
  );
  if (new Set(ids).size !== ids.length) {
    throw new TypeError("BOM replacement draft components are invalid");
  }
  return components;
}

function canonicalLocationQuery(
  locationId: StockyPublicBigIntId | null,
): { location_id: string } | undefined {
  return locationId === null
    ? undefined
    : { location_id: canonicalStockyPublicBigIntId(locationId) };
}

function kitPath(shopifyVariantId: StockyPublicBigIntId): string {
  return `/api/bom/${encodeURIComponent(
    canonicalStockyPublicBigIntId(shopifyVariantId),
  )}`;
}

/**
 * キット/コンポーネント選択 UI 用に、この店の全 variant を返す（Pro 看板機能）。
 *
 * `locationId` は在庫数の絞り込み用（Business 限定）。null=未選択（バックエンドが主拠点へ解決）。
 */
export async function listBomVariants(
  locationId: StockyPublicBigIntId | null = null,
): Promise<BomVariantOption[]> {
  const result = await apiFetch<unknown>("/api/bom/variants", {
    query: canonicalLocationQuery(locationId),
  });
  if (!Array.isArray(result)) {
    throw new TypeError("BOM variant list response is invalid");
  }
  return result.map(normalizeVariantOption);
}

/** コンポーネントが設定済みの全キットを取得する（buildable_quantity 昇順）。 */
export async function listConfiguredKits(
  locationId: StockyPublicBigIntId | null = null,
): Promise<KitBom[]> {
  const result = await apiFetch<unknown>("/api/bom", {
    query: canonicalLocationQuery(locationId),
  });
  if (!Array.isArray(result)) {
    throw new TypeError("BOM kit list response is invalid");
  }
  return result.map(normalizeKit);
}

/** 1キットの構成を取得する。variant が未設定でもキット自体が存在すれば components=[] で返る。 */
export async function getKitBom(
  shopifyVariantId: StockyPublicBigIntId,
): Promise<KitBom> {
  return normalizeKit(await apiFetch<unknown>(kitPath(shopifyVariantId)));
}

/**
 * Save the BOM and register the same relationships on the Shopify product
 * variant. Shopify then calculates the bundle inventory from its components.
 */
export async function replaceKitComponentsAndReflect(
  shopifyVariantId: StockyPublicBigIntId,
  components: BomComponentInput[],
): Promise<KitBom> {
  const body = {
    components: components.map((component) => ({
      ...component,
      component_shopify_variant_id: canonicalStockyPublicBigIntId(
        component.component_shopify_variant_id,
      ),
    })),
  };
  return normalizeKit(
    await apiFetch<unknown>(`${kitPath(shopifyVariantId)}/shopify-bundle`, {
      method: "PUT",
      body,
    }),
  );
}

/**
 * Acquire the durable server authority before opening Shopify's native
 * product-create activity. Replays and other tabs receive the same token with
 * `should_create_product=false` and must reconcile by sync only.
 */
export async function acquireStaleKitReplacementClaim(
  sourceShopifyVariantId: StockyPublicBigIntId,
): Promise<BomStaleReplacementClaim> {
  const sourceId = canonicalStockyPublicBigIntId(sourceShopifyVariantId);
  const value = await apiFetch<unknown>(
    `${kitPath(sourceId)}/replacement-claim`,
    {
      method: "POST",
      body: { draft_protocol: "server_product_set_v1" },
    },
  );
  return normalizeReplacementClaim(value, sourceId);
}

/**
 * Idempotently create or reconcile the claim-owned DRAFT product. The server
 * is the only creator and binds its exact product/variant before returning
 * `ready`; a browser retry therefore never creates a second Shopify product.
 */
export async function prepareStaleKitReplacementDraft(
  sourceShopifyVariantId: StockyPublicBigIntId,
  claimToken: string,
): Promise<BomStaleReplacementDraft> {
  const sourceId = canonicalStockyPublicBigIntId(sourceShopifyVariantId);
  const value = await apiFetch<unknown>(
    `${kitPath(sourceId)}/replacement-claim/draft`,
    {
      method: "POST",
      body: {
        claim_token: canonicalBomReplacementClaimToken(claimToken),
      },
    },
  );
  const claim = normalizeReplacementClaim(value, sourceId);
  const raw = value as Record<string, unknown>;
  if (claim.draft_protocol !== "server_product_set_v1") {
    throw new TypeError("BOM replacement draft protocol is invalid");
  }
  if (typeof raw.all_components_current !== "boolean") {
    throw new TypeError("BOM replacement draft response is invalid");
  }
  const components = normalizeDraftComponents(raw.components);
  if (
    (claim.draft_state !== "ready" &&
      (raw.all_components_current || components.length > 0)) ||
    (claim.draft_state === "ready" &&
      raw.all_components_current &&
      components.length < 1)
  ) {
    throw new TypeError("BOM replacement draft state is invalid");
  }
  return {
    ...claim,
    all_components_current: raw.all_components_current,
    components,
  };
}

/** Release only the exact, still-unbound claim owned by this create activity. */
export async function cancelStaleKitReplacementClaim(
  sourceShopifyVariantId: StockyPublicBigIntId,
  claimToken: string,
): Promise<void> {
  const sourceId = canonicalStockyPublicBigIntId(sourceShopifyVariantId);
  await apiFetch<unknown>(
    `${kitPath(sourceId)}/replacement-claim/cancel`,
    {
      method: "POST",
      body: {
        claim_token: canonicalBomReplacementClaimToken(claimToken),
      },
    },
  );
}

/**
 * Explicitly apply a stale local BOM to a newly created current Shopify
 * variant without overwriting a BOM that may have appeared on that target
 * meanwhile.
 *
 * This route is invoked only by the merchant's explicit editor action. The
 * backend re-checks both the stale source and the empty/exact target in the
 * same transaction before writing the Shopify bundle. The legacy replacement
 * route is intentionally unreachable from the current SPA.
 */
export async function explicitlyApplyStaleKitReplacementAndReflect(
  sourceShopifyVariantId: StockyPublicBigIntId,
  targetShopifyVariantId: StockyPublicBigIntId,
  components: BomComponentInput[],
  claimToken: string,
): Promise<KitBom> {
  const sourceId = canonicalStockyPublicBigIntId(sourceShopifyVariantId);
  const body = {
    claim_token: canonicalBomReplacementClaimToken(claimToken),
    target_shopify_variant_id: canonicalStockyPublicBigIntId(
      targetShopifyVariantId,
    ),
    components: components.map((component) => ({
      component_shopify_variant_id: canonicalStockyPublicBigIntId(
        component.component_shopify_variant_id,
      ),
      quantity_per: component.quantity_per,
    })),
  };
  return normalizeKit(
    await apiFetch<unknown>(
      `${kitPath(sourceId)}/replacement-shopify-bundle/explicit-apply`,
      { method: "PUT", body },
    ),
  );
}

/**
 * Remove a stale local BOM only after the newly selected current target has
 * already been reflected to Shopify and saved locally with this exact body.
 * This endpoint performs no Shopify write and is safe to retry by itself.
 */
export async function cleanupStaleKitAfterReplacement(
  sourceShopifyVariantId: StockyPublicBigIntId,
  targetShopifyVariantId: StockyPublicBigIntId,
  components: BomComponentInput[],
  claimToken: string,
): Promise<void> {
  const sourceId = canonicalStockyPublicBigIntId(sourceShopifyVariantId);
  const body = {
    claim_token: canonicalBomReplacementClaimToken(claimToken),
    target_shopify_variant_id: canonicalStockyPublicBigIntId(
      targetShopifyVariantId,
    ),
    components: components.map((component) => ({
      component_shopify_variant_id: canonicalStockyPublicBigIntId(
        component.component_shopify_variant_id,
      ),
      quantity_per: component.quantity_per,
    })),
  };
  await apiFetch<unknown>(
    `${kitPath(sourceId)}/cleanup-stale`,
    { method: "POST", body },
  );
}

/**
 * Remove only the confirmed UreyukiBox BOM for a parent that no longer exists
 * in Shopify. Any Shopify product or claim-owned draft is deliberately kept.
 * The exact component snapshot gives the backend compare-and-delete semantics.
 */
export async function deleteStaleKitLocalConfiguration(
  sourceShopifyVariantId: StockyPublicBigIntId,
  components: BomComponentInput[],
  claimToken: string | null,
): Promise<void> {
  const sourceId = canonicalStockyPublicBigIntId(sourceShopifyVariantId);
  const seen = new Set<string>();
  const canonicalComponents = components.map((component) => {
    const componentId = canonicalStockyPublicBigIntId(
      component.component_shopify_variant_id,
    );
    if (
      !Number.isSafeInteger(component.quantity_per) ||
      component.quantity_per < 1 ||
      seen.has(componentId)
    ) {
      throw new TypeError("BOM stale-delete components are invalid");
    }
    seen.add(componentId);
    return {
      component_shopify_variant_id: componentId,
      quantity_per: component.quantity_per,
    };
  });
  await apiFetch<unknown>(`${kitPath(sourceId)}/delete-stale-local`, {
    method: "POST",
    body: {
      components: canonicalComponents,
      claim_token:
        claimToken === null
          ? null
          : canonicalBomReplacementClaimToken(claimToken),
    },
  });
}
